<script setup>
import { h, ref, computed, onMounted, watch } from 'vue';
import {
  provideSidebarContext,
  useSidebarResize,
  useSidebarOculta,
} from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize } from '@vueuse/core';
// custom: CRM settings composable (flag gate for nav link — T12)
import { useCrmSettings } from 'dashboard/routes/dashboard/cw_custom/composables/useCrmSettings.js';
// custom (CRM v2 · Fase 2): papel do usuário — gate do sub-item "Funis" (admin-only).
import { useAdmin } from 'dashboard/composables/useAdmin';
// custom: filtro puro que esconde a inbox interna ("Equipe Modeloja") dos Canais
import { rejectInternalInboxes } from 'dashboard/routes/dashboard/cw_custom/composables/internalInboxFilter.js';

import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import {
  SIDEBAR_SORT_SECTIONS,
  getSidebarSortOptions,
  resolveSidebarSort,
  sortSidebarItems,
} from 'dashboard/helper/sidebarSort';
// custom: full Modeloja wordmark for the expanded sidebar header (task #4)
import ModelojaWordmark from 'dashboard/components-next/cw_custom/ModelojaWordmark.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud } = useAccount();

// custom (2026-08-08): `isCallsAvailable` (o gate enterprise de Chamadas) saiu junto com o
// item de menu — ver o bloco de nav. Se Chamadas voltar, ele volta com ela.
const store = useStore();
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const hasFilteredUnreadCounts = computed(() => {
  return (
    hasConversationUnreadCounts.value &&
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.UNREAD_COUNT_FOR_FILTERS
    )
  );
});

const hasDataImport = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.DATA_IMPORT
  );
});

// custom (2026-08-04): Captain e Central de Ajuda eram desenhados na sidebar SEM
// nenhum gatilho — desligar a função na conta não os escondia, e os dois apareciam
// mesmo em operação que não os usa. Agora seguem a função da conta, como todo o resto:
// desligou a função, some o menu; ligou, volta. As rotas continuam vivas (mesmo
// tratamento dado a Campanhas), então deep-link segue funcionando.
const hasCaptain = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CAPTAIN
  );
});

// custom (2026-08-09): `hasHelpCenter` saiu junto com o item "Central de Ajuda" da barra —
// ver a nota no bloco de nav. A função da conta segue ligada (o widget do site depende dela);
// o que saiu foi o atalho de menu, não a função.

// custom: CRM nav gate — reads crm_account_settings.enabled (our flag, not FEATURE_FLAGS.CRM).
// FEATURE_FLAGS.CRM ('crm') gates the upstream Chatwoot contacts feature — different thing.
// internalInboxIds: ids da(s) inbox(es) interna(s) ("Equipe Modeloja") a esconder dos Canais.
// custom (2026-08-09): `notasEnabled` é a flag por conta da aba Notas (off por padrão).
const {
  crmEnabled: hasCrmEnabled,
  internalInboxIds,
  notasEnabled: hasNotasEnabled,
  notificacoesEnabled: hasNotificacoesEnabled,
  fluxosEnabled: hasFluxosEnabled,
} = useCrmSettings(accountId);

// custom (CRM v2 · Fase 2): "Funis" (config) só aparece para administradores.
// Pipeline e Métricas aparecem para os dois papéis. Defesa em profundidade: o
// router e o Pundit (backend) também barram operador na config.
const { isAdmin } = useAdmin();

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const fetchSidebarSortPreferences = ([currentAccountId, userId]) => {
  if (!currentAccountId || !userId) return;
  store.dispatch('sidebarSortPreferences/initialize');
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

// custom: sidebar width is now a fixed, viewport-responsive value (task #6).
const { sidebarWidth, isCollapsed } = useSidebarResize();
// custom (2026-08-09): ocultar/mostrar a barra inteira. NÃO confundir com `isCollapsed`,
// que é o trilho só-de-ícones do upstream e segue desligado — ver a nota no provider.
const { sidebarOculta, alternar: alternarSidebar } = useSidebarOculta();

// custom (2026-08-09): O PAR DE ATALHOS DO TOPO — paleta de comandos + busca.
//
// Estrutura copiada da referencia que o Gustavo trouxe (Basepoint): um controle largo de
// "acoes rapidas" com a tecla ao lado, e um botao QUADRADO de busca colado nele. Antes havia
// so a busca ocupando a linha inteira.
//
// A paleta ja existia e so era alcancavel por atalho de teclado — invisivel para quem nao
// conhece o atalho, que e justamente quem mais precisaria dela.
//
// ⚠️ `document.querySelector('ninja-keys')`: a paleta e um CUSTOM ELEMENT montado la no
// Dashboard.vue, fora da arvore desta sidebar. Nao ha store nem evento para abri-la, entao
// o caminho e pegar o elemento e chamar `open()`. Verificado no navegador antes de escrever.
// Guarda de existencia porque o componente e carregado sob demanda: nos primeiros
// milissegundos da tela ele pode nao estar no DOM, e um clique ali nao pode estourar.
const ehMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
const teclaPaleta = ehMac ? '⌘K' : 'Ctrl K';

const abrirPaleta = () => {
  const paleta = document.querySelector('ninja-keys');
  if (paleta && typeof paleta.open === 'function') paleta.open();
};

// custom (task #4): a wordmark substitui o nome da conta no header, mas a TROCA
// de conta não pode sumir. Quando o usuário pertence a mais de uma conta, o
// SidebarAccountSwitcher aparece ao lado da wordmark (em conta única — caso
// Modeloja — só a wordmark é exibida).
const userAccounts = useMapGetter('getUserAccounts');
const showAccountSwitcher = computed(
  () => (userAccounts.value || []).length > 1
);

// custom (2026-08-11): PARA ONDE A MARCA LEVA — "Início", a primeira aba da barra.
// Espelha exatamente o gate do item de menu `Inicio` (`hasCrmEnabled`): quando o CRM está
// ligado, a marca e a primeira aba apontam para a MESMA rota, que é a única forma de a
// promessa "voltar ao começo" ser verdadeira. Com o CRM desligado o item Início não é
// desenhado, então a marca cai em `home` — o começo do produto naquele arranjo.
// Ver a nota no template para o defeito que isto conserta.
const destinoDaMarca = computed(() =>
  hasCrmEnabled.value
    ? accountScopedRoute('crm_metas_home')
    : accountScopedRoute('home')
);

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// custom: the sidebar is no longer user-resizable (task #6). isResizing is kept
// as a constant false so the shared context shape stays intact for child
// components (e.g. SidebarGroup reads it to skip toggling mid-drag).
const isResizing = ref(false);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const allUnreadCount = useMapGetter(
  'conversationUnreadCounts/getAllUnreadCount'
);
const getFolderUnreadCount = useMapGetter(
  'conversationUnreadCounts/getFolderUnreadCount'
);
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);
const getLabelUnreadCount = useMapGetter(
  'conversationUnreadCounts/getLabelUnreadCount'
);
const getTeamUnreadCount = useMapGetter(
  'conversationUnreadCounts/getTeamUnreadCount'
);
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const getSidebarSectionSort = useMapGetter(
  'sidebarSortPreferences/getSectionSort'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

// custom (2026-08-09, Lote 2): o contador do badge de Notificações.
//
// Fica num `watch` sobre (conta, flag) e não no `onMounted` acima por dois motivos, ambos
// já aprendidos neste arquivo: a flag chega DEPOIS do primeiro paint (o `crm_settings` é
// assíncrono), e a conta pode trocar sem remontar a sidebar. Buscar só na montagem daria um
// badge zerado para sempre em quem entra direto numa conta com a feature ligada.
//
// Com a flag desligada, zera em vez de chamar: o endpoint responderia 404, e pedir para
// receber 404 a cada troca de conta é ruído no log de produção.
watch(
  [accountId, hasNotificacoesEnabled],
  ([id, ligado]) => {
    if (!id) return;
    if (ligado) {
      store.dispatch('cwCustomCrmNotificacoes/fetchUnreadCount', id);
    } else {
      store.dispatch('cwCustomCrmNotificacoes/setUnreadCount', 0);
    }
  },
  { immediate: true }
);

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

watch([accountId, currentUserId], fetchSidebarSortPreferences, {
  immediate: true,
});

const hasUnreadCountsForSection = section => {
  if (section === SIDEBAR_SORT_SECTIONS.FOLDERS) {
    return hasFilteredUnreadCounts.value;
  }

  return hasConversationUnreadCounts.value;
};

const getSortOptionsForSection = section =>
  getSidebarSortOptions(section, {
    hasUnreadCounts: hasUnreadCountsForSection(section),
  });

const getSortForSection = section =>
  resolveSidebarSort(section, getSidebarSectionSort.value(section), {
    hasUnreadCounts: hasUnreadCountsForSection(section),
  });

const updateSortPreference = (section, sortBy) => {
  store.dispatch('sidebarSortPreferences/setSectionSort', {
    section,
    sortBy,
  });
};

const buildSortConfig = section => ({
  sortOptions: getSortOptionsForSection(section),
  activeSort: getSortForSection(section),
  onSortChange: sortBy => updateSortPreference(section, sortBy),
});

const sortedFolders = computed(() =>
  sortSidebarItems(conversationCustomViews.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.FOLDERS),
    labelKey: view => view.name,
    unreadCountKey: view => getFolderUnreadCount.value(view.id),
  })
);

const sortedTeams = computed(() =>
  sortSidebarItems(teams.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.TEAMS),
    labelKey: team => team.name,
    unreadCountKey: team => getTeamUnreadCount.value(team.id),
  })
);

// custom: a inbox interna ("Equipe Modeloja") não aparece na lista de Canais.
// Continua acessível por link direto — só sai da lista, mesma doutrina das demais
// exclusões do canal interno (T1). Filtra ANTES de ordenar. Lógica (e coerção de
// id) testada em internalInboxFilter.spec.js.
const visibleInboxes = computed(() =>
  rejectInternalInboxes(inboxes.value, internalInboxIds.value)
);

const sortedInboxes = computed(() =>
  sortSidebarItems(visibleInboxes.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.CHANNELS),
    labelKey: inbox => inbox.name,
    unreadCountKey: inbox => getInboxUnreadCount.value(inbox.id),
  })
);

const sortedLabels = computed(() =>
  sortSidebarItems(labels.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.LABELS),
    labelKey: label => label.title,
    unreadCountKey: label => getLabelUnreadCount.value(label.id),
  })
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

// custom (2026-06-23): helpers de rotas de Relatórios removidos junto com a aba
// "Relatórios" (ver bloco de nav abaixo). As rotas reports_* seguem vivas (deep-link).

const menuItems = computed(() => {
  return [
    // custom (Metas · F4): "Início" — 1º item da sidebar e home de entrada de todos
    // os usuários (landing wired em conversation.routes.js). Gated por CRM ligado
    // (a tela é a Home do Operador / Metas); com CRM off não há home a mostrar, o que
    // é coerente com o fail-open do guard. NÃO é um sub-item do CRM.
    ...(hasCrmEnabled.value
      ? [
          {
            name: 'Inicio',
            label: 'Início',
            icon: 'i-lucide-house',
            to: accountScopedRoute('crm_metas_home'),
            activeOn: ['crm_metas_home'],
          },
        ]
      : []),
    // custom (2026-08-09): AI MODELOJA — a tela do assistente, com porta própria.
    //
    // Ela já existia (rota `crm_assistente`, tela cheia) e só era alcançável escrevendo no
    // campo da Home. Duas coisas erradas nisso, e o Gustavo apontou as duas: quem já sabe o
    // que quer perguntar tinha de passar pela Home primeiro, e a conversa anterior não tinha
    // como ser reencontrada — não havia caminho de volta que não fosse recomeçar.
    //
    // A nota antiga em inicio.routes.js dizia para NÃO pôr no menu, com o argumento de que
    // "um item para uma tela que só responde cinco perguntas prometeria mais do que ela
    // entrega". O argumento caiu junto com o defeito do campo: agora a tela aceita qualquer
    // pergunta e responde honestamente o que não sabe (ver `responderLivre`), então a porta
    // não promete além do que existe.
    //
    // ⚠️ A POSIÇÃO na barra é declarada em NAV_ORDER, não aqui — e ela já mudou duas vezes
    // só em 2026-08-11. Hoje o item vem logo depois de Notificações, por decisão dele.
    // Não deduzir a ordem deste comentário: ler NAV_ORDER.
    ...(hasCrmEnabled.value
      ? [
          {
            name: 'Assistente',
            label: 'AI Modeloja',
            icon: 'i-lucide-sparkles',
            to: accountScopedRoute('crm_assistente'),
            activeOn: ['crm_assistente'],
          },
        ]
      : []),
    // custom (2026-08-09): NOTAS — o bloco pessoal do operador. Item top-level, não
    // sub-item do CRM: o CRM é o processo da empresa, isto é o caderno da pessoa.
    // DOIS gates, e os dois precisam estar ligados: o CRM da conta (a aba vive no módulo)
    // e a flag própria `crm_notas`, off por padrão como toda feature nova. A posição na
    // barra é declarada em NAV_ORDER, não aqui.
    ...(hasCrmEnabled.value && hasNotasEnabled.value
      ? [
          {
            name: 'Notas',
            label: 'Notas',
            icon: 'i-lucide-notebook-pen',
            to: accountScopedRoute('crm_notas'),
            activeOn: ['crm_notas'],
          },
        ]
      : []),
    // custom (2026-08-09, Lote 2): NOTIFICAÇÕES — a caixa única.
    //
    // ⚠️ Ela SUBSTITUI o sino do core na navegação, e é essa a decisão do Gustavo: uma caixa
    // só. A rota `inbox_view` do upstream continua viva por link direto, mas o conteúdo dela
    // já aparece AQUI dentro (o NotificationReader junta as duas fontes) — deixar as duas no
    // menu faria a pessoa olhar sempre a errada, que foi exatamente o que ele quis evitar.
    //
    // O badge vem do Vuex ('cwCustomCrmNotificacoes'), e não de um ref, porque o
    // SidebarGroupHeader lê o número por `useMapGetter`. O número soma as DUAS fontes e é
    // calculado no servidor.
    ...(hasCrmEnabled.value && hasNotificacoesEnabled.value
      ? [
          {
            name: 'Notificacoes',
            label: 'Notificações',
            icon: 'i-lucide-bell',
            to: accountScopedRoute('crm_notificacoes'),
            activeOn: ['crm_notificacoes'],
            getterKeys: {
              count: 'cwCustomCrmNotificacoes/getUnreadCount',
            },
          },
        ]
      : []),
    // custom (equipe interna): com CRM ligado, "Caixa de Entrada" deixa de ser a Inbox de
    // NOTIFICAÇÕES (inbox_view) e passa a ser a lista de conversas da EQUIPE INTERNA
    // (crm_internal_inbox, internal_scope='only'). A rota inbox_view segue viva por deep link;
    // só sai do menu. O badge de não-lidas era notifications/getUnreadCount — não bate mais com
    // "conversas internas não lidas", então é removido (não mentir número). Sem CRM, o item é
    // idêntico ao upstream. Mesmo gate (hasCrmEnabled) dos demais itens custom da sidebar.
    // custom (2026-06-26): label "Equipe" quando CRM ligado (SIDEBAR.INTERNAL_TEAM);
    // "Caixa de Entrada" (SIDEBAR.INBOX) quando CRM desligado — sem tocar a chave global.
    hasCrmEnabled.value
      ? {
          name: 'Inbox',
          label: t('SIDEBAR.INTERNAL_TEAM'), // custom:
          // custom (2026-08-06): era `inbox`, o MESMO ícone de Conversas › Todas as
          // conversas. Dois itens de menu com o mesmo desenho obrigam a ler o rótulo
          // para saber onde se está — e um ícone que não distingue não está trabalhando.
          // Aqui o item é o canal interno do time, então o desenho é gente.
          icon: 'i-lucide-users-round',
          to: accountScopedRoute('crm_internal_inbox'),
          activeOn: ['crm_internal_inbox', 'crm_internal_inbox_conversation'],
        }
      : {
          name: 'Inbox',
          label: t('SIDEBAR.INBOX'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('inbox_view'),
          activeOn: ['inbox_view', 'inbox_view_conversation'],
          getterKeys: {
            count: 'notifications/getUnreadCount',
          },
        },
    // ⚠️ A ABA "QUALIFICAÇÃO" (Triagem) FOI REMOVIDA em 2026-08-09, e não deve voltar
    // nesta forma. Ela tinha dois filhos: a FILA (que reusava o `ConversationView` do core)
    // e o DESEMPENHO.
    //
    // O motivo é do Gustavo, vendo a tela: "ela tá com o mesmo layout de uma conversa e a
    // administradora não vai responder, então está esquisito aquele layout". O desenho
    // prometia uma caixa de atendimento e entregava uma tela de supervisão — e quem abria
    // procurava o campo de resposta que não existe.
    //
    // NADA se perdeu, tudo mudou de lugar: a fila virou o FEED do fluxo (raias por
    // desfecho, em `crm_fluxo`), e o Desempenho virou o botão "Métricas" dentro dele —
    // "as métricas têm que viver dentro do fluxo", palavras dele. Ver fluxos.routes.js.
    //
    // custom (2026-08-09, Lote 3): FLUXOS DE TRABALHO — as automações, admin-only.
    //
    // TRÊS portões, e os três precisam passar: CRM ligado, flag `crm_fluxos` da conta, e o
    // papel de administrador. O portão que vale é a CrmFlowPolicy no servidor (401 para
    // operador); estes aqui só evitam oferecer uma porta que não abre.
    ...(hasCrmEnabled.value && hasFluxosEnabled.value && isAdmin.value
      ? [
          {
            name: 'Fluxos',
            label: 'Fluxos de trabalho',
            // Nó com ramificações: é literalmente o que um fluxo é, e não colide com
            // nenhum outro ícone da barra (o funil já é o CRM, a placa era da Triagem).
            icon: 'i-lucide-workflow',
            to: accountScopedRoute('crm_fluxos'),
            activeOn: ['crm_fluxos', 'crm_fluxo'],
          },
        ]
      : []),
    // custom (2026-08-29): a Saúde do WhatsApp SAIU daqui e foi para o grupo de
    // configurações, ao lado da Trilha de auditoria. Era item de primeiro nível da barra
    // principal; virou item de administração, que é o que ela sempre foi — ninguém abre
    // diagnóstico de caixa no meio do atendimento.
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      // custom (2026-08-06): balão REDONDO no lugar do quadrado (`message-square`).
      // O Gustavo pediu a silhueta que lembra a de mensageria — e ela é redonda, não
      // retangular. Vale além do gosto: o quadrado é o único ícone da navegação com cantos
      // retos de ponta a ponta, e num conjunto onde casa, telefone, pessoa e funil são todos
      // de traço arredondado, ele lia como peça de outro jogo.
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-lucide-inbox',
          badgeCount: allUnreadCount.value,
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        // custom: Modeloja — sub-item "Menções" (Mentions) removido do menu de Conversas
        // a pedido do produto. A rota conversation_mentions permanece viva (deep link
        // continua funcionando); apenas a entrada de menu some — mesma doutrina das demais
        // exclusões abaixo.
        // custom: Modeloja — abas "Participantes" (Participating) e "Não atendidas"
        // (Unattended) removidas do menu de Conversas a pedido do produto. As rotas
        // conversation_participating / conversation_unattended permanecem vivas (deep links
        // continuam funcionando); apenas não há mais entrada de menu para elas.
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.FOLDERS),
          collapsible: true,
          showTreeLine: true,
          children: sortedFolders.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            badgeCount: hasFilteredUnreadCounts.value
              ? getFolderUnreadCount.value(view.id)
              : 0,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.TEAMS),
          collapsible: true,
          showTreeLine: true,
          children: sortedTeams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            badgeCount: getTeamUnreadCount.value(team.id),
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.CHANNELS),
          collapsible: true,
          showTreeLine: true,
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            badgeCount: getInboxUnreadCount.value(inbox.id),
            icon: h(ChannelIcon, { inbox, class: 'size-[15px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
                badgeCount: leafProps.badgeCount,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.LABELS),
          collapsible: true,
          showTreeLine: true,
          children: sortedLabels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            badgeCount: getLabelUnreadCount.value(label.id),
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    // custom (2026-08-04): Captain só aparece quando a função está ligada na conta.
    ...(hasCaptain.value
      ? [
          {
            name: 'Captain',
            icon: 'i-woot-captain',
            label: t('SIDEBAR.CAPTAIN'),
            activeOn: ['captain_assistants_create_index'],
            children: [
              {
                name: 'Overview',
                label: t('SIDEBAR.CAPTAIN_OVERVIEW'),
                activeOn: ['captain_assistants_overview_index'],
                to: accountScopedRoute('captain_assistants_index', {
                  navigationPath: 'captain_assistants_overview_index',
                }),
              },
              {
                name: 'FAQs',
                label: t('SIDEBAR.CAPTAIN_RESPONSES'),
                activeOn: [
                  'captain_assistants_responses_index',
                  'captain_assistants_responses_pending',
                ],
                to: accountScopedRoute('captain_assistants_index', {
                  navigationPath: 'captain_assistants_responses_index',
                }),
              },
              {
                name: 'Documents',
                label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
                activeOn: ['captain_assistants_documents_index'],
                to: accountScopedRoute('captain_assistants_index', {
                  navigationPath: 'captain_assistants_documents_index',
                }),
              },
              {
                name: 'Scenarios',
                label: t('SIDEBAR.CAPTAIN_SCENARIOS'),
                activeOn: ['captain_assistants_scenarios_index'],
                to: accountScopedRoute('captain_assistants_index', {
                  navigationPath: 'captain_assistants_scenarios_index',
                }),
              },
              {
                name: 'Playground',
                label: t('SIDEBAR.CAPTAIN_PLAYGROUND'),
                activeOn: ['captain_assistants_playground_index'],
                to: accountScopedRoute('captain_assistants_index', {
                  navigationPath: 'captain_assistants_playground_index',
                }),
              },
              {
                name: 'Inboxes',
                label: t('SIDEBAR.CAPTAIN_INBOXES'),
                activeOn: ['captain_assistants_inboxes_index'],
                to: accountScopedRoute('captain_assistants_index', {
                  navigationPath: 'captain_assistants_inboxes_index',
                }),
              },
              {
                name: 'Tools',
                label: t('SIDEBAR.CAPTAIN_TOOLS'),
                activeOn: ['captain_tools_index'],
                to: accountScopedRoute('captain_assistants_index', {
                  navigationPath: 'captain_tools_index',
                }),
              },
              {
                name: 'Settings',
                label: t('SIDEBAR.CAPTAIN_SETTINGS'),
                activeOn: [
                  'captain_assistants_settings_index',
                  'captain_assistants_guidelines_index',
                  'captain_assistants_guardrails_index',
                ],
                to: accountScopedRoute('captain_assistants_index', {
                  navigationPath: 'captain_assistants_settings_index',
                }),
              },
            ],
          },
        ]
      : []),
    // custom (2026-08-08): CHAMADAS saiu da navegação. A operação não usa ligação — o canal
    // é WhatsApp de ponta a ponta. Mesma doutrina de Campanhas e Relatórios: a rota
    // `calls_dashboard_index` continua viva (deep link funciona), só o atalho some da barra.
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      // custom (redesign Modeloja · F1): brand nav icon per the shell redline.
      // custom (2026-08-06): `contact-round` desenha uma pessoa DENTRO de um cartão —
      // três elementos empilhados no mesmo quadrado de 20px, e num tamanho desse a
      // moldura vira ruído em volta da figura. `user-round` diz a mesma coisa com dois.
      icon: 'i-lucide-user-round',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          // custom (2026-08-08): "Ativo" saiu daqui e virou FILTRO dentro da tela, por isso
          // a rota dele entra no `activeOn` — quem está no recorte de ativos continua vendo
          // "Todos os Contatos" aceso na navegação, que é onde ele de fato está.
          activeOn: [
            'contacts_dashboard_index',
            'contacts_dashboard_active',
            'contacts_edit',
          ],
        },
        // ⚠️ O ITEM "Ativo" SAIU DA NAVEGAÇÃO (2026-08-08). Ele era uma subaba para UM
        // recorte da mesma lista, e o Gustavo apontou o defeito que isso criava: "quando eu
        // entro em Todos os Contatos aparece um gráfico de Contatos Ativos, está mal
        // definido... é mais fácil um filtro do que uma aba só para isso".
        // Ele tem razão e a regra é geral: recorte da MESMA lista é filtro, não item de
        // navegação — item de navegação promete outro lugar, e ali era o mesmo lugar com
        // menos linhas. A rota continua existindo (deep-link e paginação seguem valendo);
        // quem a controla agora é o filtro da tela.
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          collapsible: true,
          showTreeLine: true,
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          collapsible: true,
          showTreeLine: true,
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    // custom: CRM — visible only when crm_account_settings.enabled=true (T12).
    // Gate uses our own endpoint (/cw_custom/crm_settings), not FEATURE_FLAGS.CRM which
    // is the upstream Chatwoot contacts flag. Positioned after Contacts by design spec.
    //
    // CRM v2 · Fase 2 (spec 1.2): o item CRM vira grupo com 3 sub-itens —
    //   Pipeline (board) e Métricas do Funil para os dois papéis;
    //   Funis (config / Tela A) só para administradores (isAdmin).
    // Labels pt-BR hardcoded: o módulo CRM é pt-BR locked (v1).
    ...(hasCrmEnabled.value
      ? [
          {
            name: 'CRM',
            label: t('SIDEBAR.CRM'),
            // custom (redesign Modeloja · F1): brand nav icon per the shell redline.
            // custom (2026-08-06): `handshake` saiu. Medido contra os vizinhos da barra,
            // ele tinha 53 comandos de traço para uma média de 40 — o único fora da faixa.
            // Numa fileira, o ícone mais carregado lê como "de outra família" mesmo sendo
            // do mesmo conjunto: o que quebra a unidade é a DENSIDADE, não a fonte do
            // desenho. `funnel` resolve os dois lados — 36 comandos, dentro da faixa, e é
            // literalmente o desenho do que este menu abre.
            icon: 'i-lucide-funnel',
            children: [
              // custom (Metas · F4): "Início" saiu do grupo CRM — virou 1º item
              // top-level da sidebar (ver acima). O grupo CRM começa em Pipeline.
              {
                name: 'CRM Pipeline',
                label: 'Pipeline',
                to: accountScopedRoute('crm_board'),
                activeOn: ['crm_board'],
              },
              {
                name: 'CRM Metrics',
                label: 'Métricas',
                to: accountScopedRoute('crm_metrics'),
                activeOn: ['crm_metrics'],
              },
              // ⚠️ Setores, Metas e Internos JÁ SAÍRAM daqui uma vez (2026-08-08, manhã),
              // para Configurações, com a lógica de que trabalho e ajuste não se misturam.
              // VOLTARAM no mesmo dia, e o motivo é operacional, não estético: com eles em
              // Configurações não havia caminho para trocar de SETOR, e o Gustavo ficou sem
              // conseguir alcançar o setor de Projetos. Regra que fica: item de configuração
              // que é a única porta para um contexto de trabalho não é configuração — é
              // navegação, e navegação mora na barra.
              ...(isAdmin.value
                ? [
                    // "Setores" leva aos SETORES do negócio (Inside Sales, Projetos…); as
                    // trilhas vivem dentro deles, como nível de dentro.
                    {
                      name: 'CRM Sectors',
                      label: 'Setores',
                      to: accountScopedRoute('crm_sectors'),
                      activeOn: [
                        'crm_sectors',
                        'crm_funnels',
                        'crm_funnel_config',
                      ],
                    },
                    // custom (Metas · F3): Administração de Metas — admin-only.
                    {
                      name: 'CRM Metas',
                      label: 'Metas',
                      to: accountScopedRoute('crm_metas_admin'),
                      activeOn: ['crm_metas_admin'],
                    },
                    // custom (2026-06-26): Internos — blocklist + limpeza, admin-only.
                    {
                      name: 'CRM Internal Blocklist',
                      label: 'Internos',
                      to: accountScopedRoute('crm_internal_blocklist'),
                      activeOn: ['crm_internal_blocklist'],
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-lucide-building-2',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index', 'companies_dashboard_show'],
        },
      ],
    },
    // custom (2026-06-23): aba "Relatórios" REMOVIDA do menu de navegação por decisão de
    // produto (todos os papéis). As rotas reports_* permanecem vivas — acesso por
    // deep-link continua funcionando. Só o atalho de nav some da sidebar. (Mesmo padrão
    // da remoção de Campanhas, abaixo.)
    // custom: Campanhas removida do menu de navegação por decisão de produto (todos os
    // papéis). As rotas campaigns_* permanecem vivas — acesso por deep-link continua
    // funcionando. Só o atalho de nav some da sidebar.
    // ⚠️ A "CENTRAL DE AJUDA" (Portals) SAIU DA NAVEGAÇÃO em 2026-08-09, e não deve voltar
    // sem alguém antes ligar o produto por trás dela.
    //
    // Ela era o portal de artigos do Chatwoot — escrever base de conhecimento pública. A
    // Modeloja nunca publicou um artigo, então o item abria quatro sub-telas vazias. Palavras
    // do Gustavo: "não tem funcionalidade nenhuma".
    //
    // Em 2026-08-04 ela tinha sido posta atrás de `hasHelpCenter` (a função da conta), com a
    // ideia de que desligar a função esconderia o item. Não escondeu: a função está LIGADA
    // nesta conta e ninguém vai desligá-la só por causa do menu — mexer na função derruba
    // junto o widget de ajuda do site. Um gate que na prática nunca fecha não é um gate.
    //
    // Mesma doutrina de Campanhas, Relatórios e Chamadas: as rotas `portals_*` continuam
    // VIVAS (deep-link funciona, e quem um dia quiser publicar artigo chega lá pela URL);
    // só o atalho some da barra. O gate `hasHelpCenter` foi removido junto por ter ficado
    // sem uso — se o item voltar, ele volta com ele.
    //
    // ⚠️ NÃO confundir com o botão "Ajuda" da quina do cabeçalho (AjudaBotao.vue): aquele
    // fica, e é justamente o que resolve o que esta aba prometia. Ele oferece os caminhos
    // que EXISTEM — assistente e canal interno.
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      // custom (redesign Modeloja · F1): brand nav icon per the shell redline.
      icon: 'i-lucide-settings',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          activeOn: [
            'settings_inbox_list',
            'settings_inbox_show',
            'settings_inbox_new',
            'settings_inbox_finish',
            'settings_inboxes_page_channel',
            'settings_inboxes_add_agents',
          ],
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        ...(hasDataImport.value
          ? [
              {
                name: 'Settings Data',
                label: t('SIDEBAR.DATA'),
                icon: 'i-lucide-database',
                to: accountScopedRoute('settings_data_imports'),
              },
            ]
          : []),
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        // custom (2026-08-29): SAÚDE DO WHATSAPP, vinda da barra principal. Fica ao lado da
        // Trilha de auditoria porque as duas respondem à mesma pergunta — "o que aconteceu
        // de verdade?" — e nenhuma das duas tem ação de atendimento.
        //
        // A ROTA não mudou (`/whatsapp-saude`): só o lugar do item no menu. Mudar a URL
        // quebraria link salvo sem devolver nada em troca.
        //
        // Sem flag por conta, igual à trilha de auditoria: saber se as mensagens estão
        // chegando não é funcionalidade opcional.
        {
          name: 'WhatsappSaude',
          label: 'Saúde do WhatsApp',
          // Batimento cardíaco: é diagnóstico de sinal vital, não configuração — a
          // engrenagem diria "ajuste aqui", e não há nada para ajustar nesta tela.
          icon: 'i-lucide-activity',
          to: accountScopedRoute('whatsapp_saude'),
          activeOn: ['whatsapp_saude'],
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ];
});

// custom (handoff §Sidebar): o RODAPÉ da nav — separado dos itens principais por um spacer
// flex-1: principais colados no topo, rodapé colado embaixo (reproduz a referência
// "Sidebar Modeloja v2").
// custom (2026-08-09): 'Portals' saiu da lista junto com o item (ver a nota no bloco de nav).
// Sobrou Configurações — e o rodapé continua fazendo sentido com um item só: ele não é um
// grupo, é uma POSIÇÃO ("o que não é trabalho do dia mora embaixo").
const FOOTER_ITEM_NAMES = ['Settings'];

// custom (2026-08-08): a ORDEM da barra é declarada aqui, e não pela posição em que cada
// item foi escrito lá em cima. Motivo: `menuItems` é uma lista com blocos condicionais
// aninhados (função ligada, papel do usuário), e reordenar itens ali significa recortar e
// colar dentro de spreads — o tipo de edição que quebra um gate por descuido.
//
// Item que não estiver nesta lista cai no fim, na ordem original (é o caso de Empresas).
//
// ⚠️ A ORDEM É DITADA PELO GUSTAVO, ITEM POR ITEM (2026-08-11). Ele ditou a fila inteira de
// cabo a rabo, e ela SUBSTITUI o arranjo anterior por completo. O que a lista antiga
// defendia — "Conversas e Equipe grudadas", "Notas depois do CRM", "Fluxos fecha a barra" —
// deixou de valer; os comentários que sustentavam aquilo saíram junto, porque comentário que
// justifica uma ordem que não existe mais é pior que nenhum comentário.
//
// A lógica da fila, que é dele e é de ROTINA, não de arquitetura: ela segue o dia do vendedor
// de cima para baixo. Abre em onde-eu-estou (Início), passa pelo que chegou enquanto eu não
// estava (Notificações), põe o assistente logo em seguida — é a ele que se pergunta o que
// fazer com o que chegou —, depois vem com-quem-eu-falo (Equipe, Conversas), então a
// ferramenta pessoal (Notas), e no fim o que é estrutura da empresa e não tarefa do dia
// (Fluxos, Contatos, CRM).
//
// ⚠️ NÃO reordenar por conta própria. Esta fila é preferência declarada, não dedução — quatro
// arranjos diferentes já viveram aqui, e cada um "fazia sentido" no papel.
const NAV_ORDER = [
  'Inicio',
  'Notificacoes', // Notificações
  'Assistente', // AI Modeloja
  'Inbox', // Equipe
  'Conversation', // Conversas
  'Notas',
  'Fluxos', // Fluxos de trabalho
  'Contacts', // Contatos
  'CRM',
];
const posicaoNaBarra = nome => {
  const i = NAV_ORDER.indexOf(nome);
  return i === -1 ? NAV_ORDER.length : i;
};
const mainMenuItems = computed(() =>
  menuItems.value
    .filter(item => !FOOTER_ITEM_NAMES.includes(item.name))
    .slice()
    .sort((a, b) => posicaoNaBarra(a.name) - posicaoNaBarra(b.name))
);
const footerMenuItems = computed(() =>
  menuItems.value.filter(item => FOOTER_ITEM_NAMES.includes(item.name))
);
</script>

<template>
  <aside
    v-if="!sidebarOculta || isMobile"
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="mj-barra-lateral bg-[rgb(var(--sidebar-surface))] flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <!-- custom: search bar + compose-conversation pen button removed per product spec
         (items 1 & 2). The search route and global cmd+k handler remain intact — deep-link
         still works. Only the inline search UI and pen shortcut are gone from this sidebar.
         Section spacing tightened (mb-2/mb-3) now that there is only one row here, so the
         wordmark sits flush with the same visual rhythm as the first nav item below it. -->
    <!-- custom (redesign Modeloja · F1): wordmark header block — padding 26/22/22
         per the shell redline (docs/plans/_entregue/redesign/F1-shell-redline.md §2.1). -->
    <!-- ⚠️ A LINHA SOB A MARCA, e ela é a MESMA linha do cabeçalho da página (2026-08-08).
         Pedido do Gustavo, tirado da referência: "embaixo do nome tem uma pequena linha que
         acompanha o header da página... eu enxergo que ainda não tem a linha debaixo do nome,
         eu acho bonitinho, coloque para mim".
         O que faz a mágica não é a borda, é a ALTURA: 56px aqui e 56px no `TelaHeader` fazem
         os dois fios nascerem na mesma coordenada e lerem como um fio só atravessando a
         janela. Se um dos dois mudar de altura, a linha "quebra" no meio da tela — por isso
         os dois valores andam juntos.
         `items-center` no lugar do padding de topo: com altura fixa, centralizar é o que
         mantém a marca no meio da faixa em qualquer densidade. -->
    <section
      class="grid"
      :class="
        isEffectivelyCollapsed
          ? 'mt-3 mb-3'
          : 'h-14 items-center px-[16px] border-b border-n-weak'
      "
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <!-- custom: single full Modeloja wordmark replaces the brand icon + account
               name in the expanded header (task #4). The account switcher is appended only
               for multi-account users so switching/creating accounts is never lost. -->
          <!-- ⚠️ A MARCA LEVA PARA "INÍCIO" (2026-08-11), e não mais para `home`.
               Pedido do Gustavo: "ao clicar na wordmark, redirecionar sempre para a aba
               Início, que é a primeira aba". Ele está cobrando a convenção que todo produto
               tem — a marca é o botão de voltar ao começo —, e o destino estava errado desde
               que "Início" passou a existir: `home` é a rota de CONVERSAS do upstream, então
               clicar na marca acendia a quarta aba da barra, não a primeira. A pessoa
               "voltava para o início" e caía noutro lugar.
               O destino é calculado (`destinoDaMarca`) porque "Início" é uma tela nossa e
               existe só com o CRM ligado — ver o gate do item na lista de nav. Sem CRM não
               há Início a mostrar, e aí a marca volta a cair em `home`, que é o começo do
               produto naquele arranjo. Link que aponta para rota inalcançável é tela branca. -->
          <RouterLink
            :to="destinoDaMarca"
            class="flex items-center h-[17px] min-w-0 rounded-md focus-visible:outline-2 focus-visible:outline-n-slate-12 focus-visible:outline-offset-2"
            :class="showAccountSwitcher ? 'flex-shrink-0' : ''"
          >
            <!-- custom (2026-08-07): 22 → 17px. A marca estava com quase o dobro da altura
                 do rótulo de menu e puxava o olho para si toda vez que a tela carregava.
                 Em 17px ela fica no mesmo campo visual do resto: presente, não anunciada —
                 que é como a Attio e a Apple tratam a própria marca dentro do produto. -->
            <ModelojaWordmark
              class="h-[17px] w-auto text-n-slate-12 shrink-0"
            />
          </RouterLink>
          <SidebarAccountSwitcher
            v-if="showAccountSwitcher"
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />

          <!-- ⚠️ O BOTÃO DE RECOLHER (2026-08-09), na quina direita da fileira da marca —
               exatamente onde a referência (Basepoint) o coloca.
               O Gustavo pediu "no lugar daquela setinha de mudar de conta". O seletor de
               conta NÃO foi apagado: ele só aparece para quem tem mais de uma conta
               (`showAccountSwitcher`), e a produção tem UMA — então lá a quina fica só com
               este botão, que é o que ele quer ver. Apagá-lo de vez tiraria de quem tem
               duas contas o único caminho para trocar.
               `ml-auto` e não `justify-between` no pai: com o seletor presente, ele é quem
               deve crescer; este botão só encosta na direita. -->
          <!-- ⚠️ 18 → 20px e slate-10 → slate-11 (2026-08-09, tarde). "Está muito pequeno,
               não está nem conseguindo visualizar" — e as duas metades da queixa eram reais.
               TAMANHO: 20px é o padrão de ícone da interface, o mesmo do menu, do cabeçalho
               da página e do Ajuda. Este era o único em 18 e ficava órfão de escala.
               TINTA: slate-10 é o tom de ícone DENTRO de uma linha com rótulo ao lado, onde
               a palavra carrega a leitura. Aqui não há rótulo — o desenho é a única pista,
               e nesse papel ele tem de ser lido, não sussurrado. -->
          <!-- 🔴 `p-0` NÃO É ENFEITE — É O CONSERTO DO ÍCONE ESMAGADO (2026-08-11).
               Queixa do Gustavo: "o ícone de ocultar a barra lateral ainda tá pequeno, tem
               que ficar do tamanho da casinha do header". Ele já ESTAVA declarado no mesmo
               tamanho da casinha (`size-5`, 20px) — medido no navegador, a casinha do
               TelaHeader dava 20×20 e este dava **8×20**. Não era escolha de tamanho, era
               layout quebrado, e a diferença nasce de uma linha global:
                 `app/javascript/dashboard/assets/scss/_base.scss:44` aplica
                 `py-1 px-2.5` (4px/10px) a TODO `<button>` da dashboard.
               Com `box-sizing: border-box` e `size-7` (28px), a caixa de conteúdo sobra
               28 − 20 = **8px** na horizontal — e o ícone, sendo item flex com `shrink` 1,
               era comprimido a 8px. Na vertical sobrava 28 − 8 = 20px, exatamente o pedido,
               e é por isso que a altura parecia certa e só a largura mentia.
               ⚠️ A REGRA GERAL, porque a armadilha não é deste botão: neste codebase
               `size-N` num `<button>` NÃO entrega uma caixa quadrada de N — entrega
               (N−20)×(N−8) de conteúdo. Botão só-de-ícone precisa de `p-0` explícito.
               A casinha do header escapa por não ser `<button>`: é um `<span>`. -->
          <button
            type="button"
            class="ml-auto shrink-0 size-7 p-0 inline-flex items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-n-slate-12 focus-visible:outline-offset-2"
            :title="$t('SIDEBAR.HIDE')"
            :aria-label="$t('SIDEBAR.HIDE')"
            @click="alternarSidebar"
          >
            <span class="i-lucide-panel-left size-5 shrink-0" />
          </button>
        </template>
      </div>
    </section>
    <!-- custom (handoff §Sidebar): nav = coluna flex. Itens principais colados no topo;
         um spacer flex-1 empurra o rodapé (Central de Ajuda / Configurações) para baixo.
         Substitui o grid anterior — que, com flex-grow, espalhava as linhas (align-content:
         stretch) quando o conteúdo era curto e fazia o menu "flutuar" centralizado. -->
    <!-- ⚠️ `gap-0` E NÃO `gap-2` (2026-08-09, tarde). O gap do nav se aplicava ENTRE TODOS os
         filhos — inclusive entre a busca e o espaçador logo abaixo dela —, e era metade da
         causa do desequilíbrio que o Gustavo pegou: "o espaçamento dela entre a linha que
         divide o header para o primeiro ícone tem que ficar centralizado".
         Contas, porque este bloco só se conserta com número: acima da busca havia 11px (a
         margem dela, sem padding no nav); abaixo, 11 + 8 (gap) + 4 (espaçador) + 8 (gap) =
         31px. Quase o triplo. Agora o respiro é UMA peça só — a margem simétrica da busca —
         e nada mais se soma a ela. Ver a nota do `my-[21px]` para o porquê do 21. -->
    <nav
      class="flex flex-col overflow-y-scroll flex-grow pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <!-- custom (redesign Modeloja · F1): "Principal" eyebrow above the menu list
           (docs/plans/_entregue/redesign/F1-shell-redline.md §2.2). Hidden on the collapsed rail. -->
      <!-- custom (2026-08-06): o rótulo era `font-bold` + `uppercase` + tracking largo — o
           tratamento de etiqueta de painel de controle, e o Gustavo apontou o peso gráfico
           como incômodo. Ele tinha razão: um rótulo que só ORGANIZA a lista não pode pesar
           mais que os itens que ele organiza, e caixa-alta em negrito pesa.
           Agora é caixa baixa, peso normal e cor mais apagada — a hierarquia passa a vir do
           tamanho e do tom, que é como a Apple resolve rótulo de seção: você percebe que
           existe sem que ele peça atenção. O tracking também caiu; espaçamento largo é uma
           convenção de caixa-alta e em caixa baixa só afrouxa a palavra. -->
      <!-- custom (2026-08-08): o rótulo "Principal" SAIU. Ele nomeava um grupo que não tem
           par — não existe um "Secundário" na barra —, e rótulo de seção só trabalha quando
           separa uma seção de outra. Sozinho, ele era uma palavra ocupando a primeira linha
           da navegação sem dizer nada a ninguém. Palavras do Gustavo: "não está profissional".
           O espaço dele vira respiro entre a marca e o primeiro item, que é o que a
           referência faz: a lista começa, sem anúncio. -->
      <!-- ⚠️ O ATALHO DE BUSCA (2026-08-08). Aqui havia um espaçador vazio, cuja única
           função era empurrar a lista para alinhar com a saudação da Home. Ele pediu para
           trocar o vazio por conteúdo: "coloca um atalho igual na imagem de referência para
           a busca, no mesmo estilo — só para a gente ter também um elemento ali para usar
           como alinhamento".
           É melhor dos dois jeitos: o alinhamento continua (a peça ocupa o mesmo lugar) e o
           espaço passa a fazer alguma coisa. A busca global já existia e só era alcançável
           por atalho de teclado, o que a deixava invisível para quem não o conhece.
           A tecla `/` é a mesma da referência e a que este app já usa para busca. -->
      <!-- ⚠️ VIROU UM PAR em 2026-08-09, copiando a estrutura da referencia: acoes rapidas
           (largo, com a tecla) + busca (quadrado, colado nele). Antes a busca sozinha
           ocupava a linha toda.
           O ganho nao e de enfeite: a PALETA de comandos ja existia e so abria por atalho de
           teclado, ou seja, era invisivel exatamente para quem nao conhece o atalho. Agora
           ela tem porta, e a porta ensina a tecla. -->
      <!-- ⚠️ `my-[21px]`: O RESPIRO SIMÉTRICO DA BUSCA, e o 21 é conta, não gosto.
           Duas restrições tinham de caber no mesmo espaço e antes cada uma tinha a sua peça
           (a margem de 11px aqui, mais um espaçador de 4px embaixo) — o que garantia a
           segunda e quebrava a primeira.
             1. IGUAL DOS DOIS LADOS (pedido do Gustavo, 2026-08-09).
             2. O primeiro item do menu tem de continuar batendo com a saudação da Home —
                o alinhamento medido em 2026-08-08, que vale 74px entre o fio do cabeçalho
                e o topo da lista.
           Uma peça só resolve as duas: (74 − 32 da caixa da busca) ÷ 2 = 21. Simétrico por
           construção e com o total intacto.
           ⚠️ Depende da altura da faixa da marca (h-14) e da caixa da busca (h-8). Mexeu
           numa delas, refaça a divisão — não chute o 21. -->
      <!-- ⚠️ 21 → 12px em 2026-08-11. "O espaço tanto em cima desse atalho, a distância até a
           linha do header, quanto para o primeiro ícone, está muito grande."
           Isto REVOGA a conta do 21, e a conta merece explicação porque ela era boa: 21 vinha
           de (74 − 32) ÷ 2, onde 74 era a distância medida em 2026-08-08 entre o fio do
           cabeçalho e o topo da lista, escolhida para o primeiro item do menu bater com a
           saudação da Home. Esse alinhamento deixa de valer com 12 (o total cai de 74 para
           56), e é uma troca consciente: o alinhamento com a Home é invisível para quem usa
           — ninguém compara as duas colunas — enquanto o vazio na barra ele viu de imediato.
           Simetria mantida: o respiro continua sendo UMA peça só, igual em cima e embaixo. -->
      <div
        v-if="!isEffectivelyCollapsed"
        class="flex items-stretch gap-1.5 my-3"
      >
        <button
          type="button"
          class="mj-busca group flex flex-1 min-w-0 items-center gap-2 h-8 pl-1.5 pr-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-11 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-n-slate-12 focus-visible:outline-offset-2"
          @click="abrirPaleta"
        >
          <!-- O simbolo numa caixinha, como na referencia: ele marca que aquilo e um
               COMANDO, nao um campo de texto. Sem a caixa, o glifo lido sozinho parece
               decoracao. -->
          <span
            class="shrink-0 size-5 inline-flex items-center justify-center rounded-md border border-n-weak bg-n-solid-1"
            aria-hidden="true"
          >
            <span class="i-lucide-command size-3 text-n-slate-10" />
          </span>
          <span class="flex-1 min-w-0 text-left text-mj-13 leading-4 truncate">
            {{ $t('SIDEBAR.QUICK_ACTIONS') }}
          </span>
          <kbd
            class="shrink-0 h-5 px-1.5 inline-flex items-center justify-center rounded-md border border-n-weak bg-n-solid-1 text-mj-11 font-mono text-n-slate-10 select-none whitespace-nowrap"
          >
            {{ teclaPaleta }}
          </kbd>
        </button>

        <RouterLink
          :to="accountScopedRoute('search')"
          :aria-label="$t('SIDEBAR.SEARCH')"
          :title="$t('SIDEBAR.SEARCH')"
          class="mj-busca group shrink-0 flex items-center gap-1.5 h-8 px-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-11 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-n-slate-12 focus-visible:outline-offset-2"
        >
          <span class="i-lucide-search size-4 shrink-0" aria-hidden="true" />
          <!-- O rotulo TEXTUAL saiu: neste botao o icone ja diz "buscar", e a palavra
               roubaria a largura que as acoes rapidas usam. Ele sobrevive em `aria-label` e
               `title`, entao leitor de tela e repouso do mouse continuam servidos.
               A tecla como pastilha, igual a referencia: ensina o atalho sem uma linha de
               legenda. `select-none` porque ninguem copia uma tecla. -->
          <kbd
            class="shrink-0 h-5 min-w-5 px-1 inline-flex items-center justify-center rounded-md border border-n-weak bg-n-solid-1 text-mj-11 font-mono text-n-slate-10 select-none"
          >
            /
          </kbd>
        </RouterLink>
      </div>

      <!-- ⚠️ O ESPAÇADOR DE ALINHAMENTO SAIU (2026-08-09, tarde), e a função dele NÃO se
           perdeu: ela foi absorvida pela margem simétrica da busca (ver o `my-[21px]` acima).
           Ele existia porque o respiro e o alinhamento eram tratados como duas restrições
           disputando o mesmo espaço — uma peça para cada. Só que empilhar peças de um lado
           só é exatamente o que produz assimetria, e o Gustavo pegou o resultado na tela.
           A conta que resolve as duas de uma vez está na nota da busca. Se alguém precisar
           reintroduzir um resíduo aqui, o respiro da busca tem de cair pela metade dele —
           senão o desequilíbrio volta. -->

      <!-- ⚠️ O `gap-0` VOLTOU A SER `gap-1` (2026-08-23), e isto SUPERSEDE a medida de
           2026-08-09 registrada logo abaixo.
           Aquela nota dizia: "com 4px entre itens o passo dava 40px contra 30 da referência".
           Estava certa PARA AQUELA referência. O Gustavo trouxe outra e apontou o efeito na
           tela: sem folga nenhuma, o realce do item selecionado ENCOSTA no item seguinte, e
           os dois leem como um bloco só em vez de duas linhas.
           É a diferença entre espaçar uma LISTA e espaçar OBJETOS. Enquanto o item era só
           texto, o espaço interno da caixa bastava para separar. Com um realce de fundo
           arredondado, a caixa passou a ter borda visível — e duas caixas coladas não se
           leem como duas. 4px é o mínimo que faz o cantinho arredondado de uma respirar
           antes da outra começar; o passo vai a 38px, e é esse o preço declarado. -->
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in mainMenuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
      <!-- spacer (handoff): empurra o rodapé p/ baixo; colapsa p/ scroll quando o
           conteúdo excede a altura disponível. -->
      <div class="flex-1 min-h-[24px]" aria-hidden="true" />
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in footerMenuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-[rgb(var(--sidebar-surface))] to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <!-- custom (redesign Modeloja · F1): footer profile band — border-top hairline +
           14/16 padding per the shell redline (docs/plans/_entregue/redesign/F1-shell-redline.md §2.4). -->
      <div
        class="flex-shrink-0 flex w-full z-50 items-center border-t border-n-weak"
        :class="
          isEffectivelyCollapsed
            ? 'justify-center px-1 py-1.5'
            : 'justify-between py-[10px] px-3'
        "
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- custom: resize handle removed — sidebar width is fixed/responsive (task #6) -->
  </aside>

  <!-- ⚠️ O BOTÃO DE REABRIR. Vive FORA do `<aside>` de propósito: quando a barra está
       oculta ela não existe no DOM, então não haveria onde hospedá-lo.
       `fixed` e não dentro de alguma tela: a barra é do produto inteiro, e um botão que só
       aparecesse em algumas telas deixaria a pessoa presa nas outras — sem barra e sem
       caminho de volta, que é o pior desfecho possível deste controle.
       Só no desktop (`!isMobile`): no celular a barra já é uma gaveta com o próprio gatilho. -->
  <button
    v-if="sidebarOculta && !isMobile"
    type="button"
    class="fixed top-3 ltr:left-3 rtl:right-3 z-50 size-8 p-0 inline-flex items-center justify-center rounded-lg border border-n-weak bg-n-background text-n-slate-11 shadow-mj-1 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-n-slate-12 focus-visible:outline-offset-2"
    :title="$t('SIDEBAR.SHOW')"
    :aria-label="$t('SIDEBAR.SHOW')"
    @click="alternarSidebar"
  >
    <!-- `p-0` e `size-5`: o par que fecha com o botão de ocultar. Este sofria do MESMO
         esmagamento (`size-8` = 32 → caixa de conteúdo de 12px) e ainda pedia 18px, um
         terceiro tamanho para o mesmo desenho. Os dois controles são o mesmo gesto em
         dois estados — têm de ter o mesmo peso, senão a barra "muda de ícone" ao voltar. -->
    <span class="i-lucide-panel-left size-5 shrink-0" />
  </button>
</template>
